import { Box, Button, Chip, IconButton, Pagination, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import SkillItem from '@src/App/Components/Items/EntityItems/SkillItem/SkillItem';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type Employee from '@src/Objects/Employee';
import type { AppState } from '@store/store';
import { Columns2, FileText, HandMetal, Plus, Rows2, Sparkles, Target, TrendingUp } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import SkillsFilterHeader from '../../ProfileCard/components/FilterHeaders/SkillsFilterHeader';
import QuickLinksSection, { type QuickLinkTile } from './components/QuickLinksSection';
import StatsCards, { type StatsTile } from './components/StatsCards';
import SuggestedSkillsWidget from './components/SuggestedSkillsWidget';
import T4loAssistantInfoBox from './components/T4loAssistantInfoBox';

interface SkillManagerTabProps {
    employee: Employee;
    onTabChange?: (tabIndex: number) => void;
}

function SkillManagerTab({ employee, onTabChange }: SkillManagerTabProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // const lang = useSelector((state: AppState) => state.employee.language);
    // const langStrings = getLanguageTexts(lang);
    const reduxEmployee = useSelector((state: AppState) => state.employee);

    const [viewMode, setViewMode] = useState<'2-column' | '1-column'>('2-column');
    const [skillsViewMode, setSkillsViewMode] = useState<'list' | 'chips'>('list');

    const currentLayout = isMobile ? '1-column' : viewMode;

    const employeeSkills = reduxEmployee.skills || [];
    const superPowers = employeeSkills.filter((skill) => skill.isSuperPower === true);
    const avgSkillLevel =
        employeeSkills.length > 0
            ? employeeSkills.reduce((sum, skill) => sum + skill.level, 0) / employeeSkills.length
            : 0;

    const quickLinkTiles: QuickLinkTile[] = [
        {
            title: 'Skills Übersicht',
            caption: 'Alle Skills im Überblick',
            icon: getAppIcon(EIconCategory.SKILLS),
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.SKILLS}`),
        },
        {
            title: 'CV-Analyse',
            caption: 'Skills aus CV extrahieren',
            icon: <FileText size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_ANALYSE}`),
        },
        {
            title: 'Skills bearbeiten',
            caption: 'Skills hinzufügen/bearbeiten',
            icon: <TrendingUp size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => navigate(`/${ELinks.PROFILE_EDIT_SKILLS}`),
        },
        {
            title: 'Skill Checker',
            caption: 'Skills vergleichen',
            icon: <Target size={24} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
            onClick: () => onTabChange?.(6),
        },
    ];

    const tiles: StatsTile[] = [
        {
            title: 'Skills',
            value: employeeSkills.length.toString(),
            icon: <HandMetal size={32} strokeWidth={1.5} />,
            color: theme.palette.primary.main,
            bgColor: alpha(theme.palette.primary.main, 0.1),
            borderColor: alpha(theme.palette.primary.main, 0.3),
        },
        {
            title: 'Super Powers',
            value: superPowers.length.toString(),
            icon: <Sparkles size={32} strokeWidth={1.5} />,
            color: '#ffb74d',
            bgColor: alpha('#ffb74d', 0.1),
            borderColor: alpha('#ffb74d', 0.3),
        },
        {
            title: 'Ø Skill Level',
            value: avgSkillLevel.toFixed(1),
            icon: <TrendingUp size={32} strokeWidth={1.5} />,
            color: theme.palette.secondary.main,
            bgColor: alpha(theme.palette.secondary.main, 0.1),
            borderColor: alpha(theme.palette.secondary.main, 0.3),
        },
    ];

    const SkillManagement = () => {
        const [searchValue, setSearchValue] = useState('');
        const [selectedLevel, setSelectedLevel] = useState<number | null>(null);
        const [showSuperPowers, setShowSuperPowers] = useState(false);
        const [page, setPage] = useState(1);
        const itemsPerPage = 10;

        // Get SuperPowers from Redux employee
        const superPowerSkills = React.useMemo(() => {
            return employeeSkills.filter((skill) => skill.isSuperPower === true);
        }, [employeeSkills]);

        const filteredSkills = React.useMemo(() => {
            let skills = employeeSkills;

            if (searchValue) {
                skills = skills.filter((skill) => skill.title.toLowerCase().includes(searchValue.toLowerCase()));
            }

            if (selectedLevel !== null) {
                skills = skills.filter((skill) => skill.level === selectedLevel);
            }

            if (showSuperPowers) {
                skills = superPowerSkills;
            }

            return skills;
        }, [employeeSkills, searchValue, selectedLevel, showSuperPowers, superPowerSkills]);

        // Paginated skills for list view
        const paginatedSkills = React.useMemo(() => {
            if (skillsViewMode === 'chips') return filteredSkills;
            const startIndex = (page - 1) * itemsPerPage;
            return filteredSkills.slice(startIndex, startIndex + itemsPerPage);
        }, [filteredSkills, page, itemsPerPage, skillsViewMode]);

        const totalPages = Math.ceil(filteredSkills.length / itemsPerPage);

        // Reset page when filters change
        React.useEffect(() => {
            setPage(1);
        }, [searchValue, selectedLevel, showSuperPowers, skillsViewMode]);

        const getLevelBackgroundColor = (level: number) => {
            return theme.palette.levelBackgrounds?.[level as 1 | 2 | 3 | 4] || theme.palette.primary.main;
        };

        const getLevelBadgeColor = (level: number) => {
            return theme.palette.levels?.[level as 1 | 2 | 3 | 4] || theme.palette.primary.main;
        };

        return (
            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    bgcolor: 'background.paper',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Meine Fähigkeiten
                    </Typography>
                </Box>
                <SkillsFilterHeader
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    selectedLevel={selectedLevel}
                    onLevelChange={setSelectedLevel}
                    showSuperPowers={showSuperPowers}
                    onToggleSuperPowers={() => setShowSuperPowers(!showSuperPowers)}
                    viewMode={skillsViewMode}
                    onViewModeChange={setSkillsViewMode}
                />

                {skillsViewMode === 'chips' ? (
                    paginatedSkills.length > 0 ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {paginatedSkills.map((skill) => (
                                <Chip
                                    key={skill.id}
                                    label={skill.title}
                                    icon={
                                        <Box
                                            sx={{
                                                width: 16,
                                                height: 16,
                                                borderRadius: '50%',
                                                bgcolor: getLevelBadgeColor(skill.level),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                                color: '#333333 !important',
                                                ...(skill.level === 4 && {
                                                    boxShadow: `0 0 8px ${getLevelBadgeColor(skill.level)}, 0 0 16px ${getLevelBadgeColor(skill.level)}40`,
                                                }),
                                            }}
                                        >
                                            {skill.level}
                                        </Box>
                                    }
                                    onClick={() => navigate(`/${ELinks.SKILLS}/${skill.id}`)}
                                    sx={{
                                        bgcolor: getLevelBackgroundColor(skill.level),
                                        color:
                                            skill.level >= 3
                                                ? theme.palette.getContrastText(theme.palette.primary.main)
                                                : theme.palette.text.primary,
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: getLevelBackgroundColor(skill.level),
                                            opacity: 0.8,
                                            transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <HandMetal
                                size={48}
                                style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                            />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Keine Skills vorhanden
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Füge deine ersten Skills hinzu, um sie hier zu sehen
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<Plus />}
                                onClick={() => navigate(`/${ELinks.PROFILE_EDIT_SKILLS}`)}
                                sx={{ textTransform: 'none' }}
                            >
                                Skills hinzufügen
                            </Button>
                        </Box>
                    )
                ) : (
                    <>
                        {paginatedSkills.length > 0 ? (
                            <>
                                <Box sx={{ mt: 2 }}>
                                    {paginatedSkills.map((skill) => (
                                        <SkillItem
                                            key={skill.id}
                                            id={skill.id}
                                            title={skill.title}
                                            level={skill.level}
                                            disableOverlay={true}
                                            onClick={() => navigate(`/${ELinks.SKILLS}/${skill.id}`)}
                                        />
                                    ))}
                                </Box>

                                {/* Pagination for list view */}
                                {totalPages > 1 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                        <Pagination
                                            count={totalPages}
                                            page={page}
                                            onChange={(_, newPage) => setPage(newPage)}
                                            size="small"
                                            color="primary"
                                        />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 4 }}>
                                <HandMetal
                                    size={48}
                                    style={{ color: theme.palette.text.secondary, marginBottom: '16px' }}
                                />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                    Keine Skills vorhanden
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Füge deine ersten Skills hinzu, um sie hier zu sehen
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<Plus />}
                                    onClick={() => navigate(`/${ELinks.PROFILE_EDIT_SKILLS}`)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Skills hinzufügen
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Paper>
        );
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Skill Manager
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Verwalte deine Skills und schließe Kompetenzlücken
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => navigate(`/${ELinks.PROFILE_EDIT_SKILLS}`)}
                        aria-label="Skills bearbeiten"
                        title="Skills bearbeiten"
                        sx={{
                            color: theme.palette.primary.main,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                            },
                        }}
                    >
                        {getAppIcon(EIconCategory.EDIT)}
                    </IconButton>

                    {/* View Toggle */}
                    {!isMobile && (
                        <Box
                            sx={{
                                display: 'flex',
                                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                                borderRadius: 1,
                                ml: 1,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => setViewMode('2-column')}
                                sx={{
                                    color:
                                        viewMode === '2-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        viewMode === '2-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '4px 0 0 4px',
                                }}
                            >
                                <Columns2 size={16} />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => setViewMode('1-column')}
                                sx={{
                                    color:
                                        viewMode === '1-column'
                                            ? theme.palette.primary.main
                                            : theme.palette.text.secondary,
                                    bgcolor:
                                        viewMode === '1-column'
                                            ? alpha(theme.palette.primary.main, 0.1)
                                            : 'transparent',
                                    borderRadius: '0 4px 4px 0',
                                }}
                            >
                                <Rows2 size={16} />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Content Layout */}
            {currentLayout === '2-column' ? (
                <Box sx={{ display: 'flex', gap: 3 }}>
                    {/* Left Column */}
                    <Box sx={{ flex: '0 0 58%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <StatsCards tiles={tiles} />
                        <SkillManagement />
                    </Box>

                    {/* Right Column */}
                    <Box sx={{ flex: '0 0 38%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <T4loAssistantInfoBox context="skills" />

                        <SuggestedSkillsWidget />

                        <QuickLinksSection tiles={quickLinkTiles} layout="1-column" />
                    </Box>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <T4loAssistantInfoBox context="skills" />
                    <StatsCards tiles={tiles} />

                    {/* Skills Overview */}
                    <Box>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.secondary }}
                        >
                            Skills Übersicht
                        </Typography>
                        <SkillManagement />
                    </Box>

                    <QuickLinksSection tiles={quickLinkTiles} layout="1-column" />
                </Box>
            )}
        </Box>
    );
}

export default SkillManagerTab;
